/* eslint react/forbid-prop-types: 0 */
import React, {
  useContext, useState, memo, useEffect, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import {
  ProductPod,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductLink,
  ProductIdentifier, ProductBadge,
  ProductImage, PodSection, ProductCompare,
  ProductCustomOptions, PodSpacer
} from '@thd-olt-component-react/product-pod';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { Price } from '@thd-olt-component-react/price';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Quantity } from '@thd-olt-component-react/quantity';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { NoteButton, ProductNote } from '../note';
import { ProductListContext } from '../ProductListContext';
import { DeleteButton } from '../delete-button/DeleteButton';
import { isItemEqual, debounce, isGeneralMerchProduct } from '../productListHelper';
import { ListViewPlaceHolder } from './ListViewPlaceHolder';

export const ListPod = memo(({
  handleQuantityUpdate,
  itemId,
  podIndex,
  quantity: quantityProp,
  showATQ,
  showStoreSku,
  storeId,
  searchKeyword
}) => {
  const {
    productsDisplayed,
    isSharedList
  } = useContext(ProductListContext);
  const [quantity, setQuantity] = useState(quantityProp);

  const { loading } = useDataModel('product', {
    variables: {
      itemId,
    },
    skip: !itemId
  });

  const updateListQuantity = useMemo(() => {
    return debounce(handleQuantityUpdate, 1000);
  }, []);

  useEffect(() => {
    if (quantity !== quantityProp) {
      setQuantity(quantityProp);
    }
  }, [quantityProp]);

  const product = productsDisplayed.find((item) => item.itemId === itemId);

  function isNumeric(value) {
    return /^-?\d+$/.test(value);
  }

  const onQuantityUpdate = (newQuantity) => {
    if (isNumeric(newQuantity?.quantity) && newQuantity?.quantity !== quantity) {
      setQuantity(newQuantity?.quantity);
      updateListQuantity({ itemId, quantity: quantityProp, newQuantity, price: product?.pricing?.value });
    }
  };

  const noATCFulfillment = isGeneralMerchProduct(product);

  if (loading) {
    return (
      <ListViewPlaceHolder count={1} />
    );
  }

  return (
    <ProductPod
      itemId={itemId}
      storeId={storeId}
      padding="sui-p-2 sui-pb-0"
      analyticsData={{
        parent: 'product-list', position: podIndex
      }}
      render={(pod) => (
        <>
          <div data-testid={`product-item-${itemId}`} className="sui-grid sui-grid-cols-12 sui-gap-2">
            <div className="sui-col-span-2">
              <PodSection padding="sui-p-0" noPadding columnAlign relative>
                <PodSpacer padding={['TOP', 'BOTTOM']}>
                  <ProductImage itemId={pod.itemId} />
                </PodSpacer>
                <SuperSkuMiniSwatches
                  itemId={pod.itemId}
                  storeId={storeId}
                  onChange={pod.onChange}
                  onHover={() => {}}
                  numberOfMiniSwatches={5}
                />
              </PodSection>
            </div>
            <div className="sui-col-span-6">
              <div className="sui-flex">
                <ProductBadge itemId={pod.itemId} storeId={storeId} />
              </div>
              <PodSpacer padding={['TOP', 'BOTTOM']}>
                <ProductHeader
                  brand="inline"
                  itemId={pod.itemId}
                  brandTitleMaxLine={4}
                  disableShopThisCollection
                />
              </PodSpacer>
              <div className="sui-flex sui-flex-row">
                <ProductIdentifier itemId={pod.itemId} type="itemId" />
                <ProductIdentifier itemId={pod.itemId} type="model" />
                {showStoreSku && <ProductIdentifier itemId={pod.itemId} type="storeSku" />}
              </div>
              <ProductRating itemId={pod.itemId} />
              <PodSpacer padding={['TOP', 'BOTTOM']}>
                <Price
                  itemId={pod.itemId}
                  large={false}
                  storeId={storeId}
                  displayEachUom={false}
                  showPreferredPricingBadge
                />
              </PodSpacer>
              {
                searchKeyword && (
                  <div className="sui-text-thd-blue sui-text-xs sui-font-normal sui-py-2">
                    Searched for:
                    {' '}
                    <span className="sui-font-bold">{`"${searchKeyword}"`}</span>
                  </div>
                )
              }
              <div className="sui-w-40 sui-pb-1">
                {
                  isSharedList ? `Qty: ${quantity}` : (
                    <Quantity
                      onChange={onQuantityUpdate}
                      value={quantity}
                    />
                  )
                }
              </div>
            </div>
            <div className="sui-col-span-4">
              <div className="sui-flex sui-flex-row sui-justify-between">
                <div>
                  <PodSpacer minHeight="45px" padding={['TOP']}>
                    <FulfillmentPodStore itemId={pod.itemId} storeId={pod.storeId} />
                  </PodSpacer>
                  <PodSpacer minHeight="45px" padding={['BOTTOM']}>
                    <FulfillmentPodShipping itemId={pod.itemId} storeId={pod.storeId} />
                  </PodSpacer>
                </div>
                {
                  !isSharedList && (
                    <div className="sui-flex sui-flex-col sui-gap-2">
                      <DeleteButton iconSize="regular" itemId={itemId} />
                      <NoteButton iconSize="regular" itemId={itemId} />
                    </div>
                  )
                }
              </div>
              <PodSpacer padding={['BOTTOM']}>
                <ProductATC
                  itemId={pod.itemId}
                  checkGeneric
                  quantity={quantity}
                  noATCFulfillment={noATCFulfillment}
                />
              </PodSpacer>
              {
                showATQ && (
                  <AddToQuote
                    itemId={pod.itemId}
                    quantity={quantity}
                  />
                )
              }
            </div>
            <div className="sui-col-span-12">
              <ProductNote itemId={itemId} />
            </div>
          </div>
        </>
      )}
    />
  );
}, isItemEqual);

ListPod.displayName = 'ListPod';

ListPod.propTypes = {
  handleQuantityUpdate: PropTypes.func,
  itemId: PropTypes.string.isRequired,
  podIndex: PropTypes.number,
  quantity: PropTypes.number,
  searchKeyword: PropTypes.string,
  showATQ: PropTypes.bool,
  showStoreSku: PropTypes.bool,
  storeId: PropTypes.string
};

ListPod.defaultProps = {
  handleQuantityUpdate: () => {},
  podIndex: 1,
  quantity: 1,
  searchKeyword: '',
  showATQ: false,
  showStoreSku: true,
  storeId: ''
};

ListPod.dataModel = extend(
  NoteButton,
  ProductPod,
  ProductATC,
  ProductImage,
  ProductLink,
  ProductServiceAddOns,
  Price,
  SuperSkuMiniSwatches,
  ProductRating,
  ProductCompare,
  ProductSample,
  ProductIdentifier,
  ProductHeader,
  ProductBadge,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  ProductCustomOptions,
  NonBuyableQuoteCenter,
  AddToQuote
);
